<template>
    <div>
        <fe-dialog
            v-if="localValue"
            title="Link Progress Monitoring"
            @dismiss="localValue=false; selectedPM = null"
            @accept="doLink"
            dismissButtonText="Cancel"
            :acceptButtonText="showErrorState ? 'Got It' : 'Link Progress Monitoring'"
            persistent
            width="800"
            height="600"
            disableAutoclose
        >
            <banner 
                v-if="showBanner"
                class="mt-2 mb-5"
                usage="warning" 
                text="Select a Progress Monitoring plan to link."
            />
            <fe-spinner v-if="isLoading" class="spinner"/>
            <div v-else class="mt-3">
                <div class="d-flex align-center mb-2">
                    <v-checkbox 
                        v-model="onlyShowActive" 
                        hide-details 
                        :ripple="false"
                        class="pms-to-link-input"
                    />
                    Only show progress monitoring for the current school year
                </div>
                <v-list>
                    <v-radio-group v-model="selectedPM" hide-details class="pms-to-link-input">
                        <v-list-item v-for="item in filteredItems" 
                            class="pms-to-link mb-3" 
                            :class="{'pms-to-link-selected': selectedPM === item}"
                            @click="selectedPM = item"
                        >
                            <v-radio class="pms-to-link-radio" :value="item" :ripple="false"></v-radio>
                            <div class="mb-2">{{ item.sub_category_name || item.intervention_plan_name }}</div>
                            <div class="pms-to-link-subtext" style="margin-left: 28px;">
                                {{ item.school_year_name }}
                                <span class="mx-2">|</span> {{ item.category_name }}
                                <span class="mx-2">|</span>
                                <span v-if="item.intervention_plan_name"><v-icon class="far fa-hands-heart mr-2" color="#495B8F !important"></v-icon>{{ item.intervention_plan_name }}</span>
                                <span v-else>Not linked to an intervention</span>
                            </div>
                        </v-list-item>
                    </v-radio-group>
                </v-list>

                <error-state type="Progress Monitoring Plans" v-model="showErrorState"></error-state>

                <div v-if="existing.length">
                    Already added to this form:
                    <v-list>
                        <v-list-item v-for="linked in existing" class="pms-to-link mb-3">
                            <div class="mb-2">{{ linked.sub_category_name || linked.intervention_plan_name }}</div>
                            <div class="pms-to-link-subtext">
                                {{ linked.school_year_name }}
                                <span class="mx-2">|</span> {{ linked.category_name }}
                                <span class="mx-2">|</span>
                                <span v-if="linked.intervention_plan_name"><v-icon class="far fa-hands-heart mr-2" color="#495B8F !important"></v-icon>{{ linked.intervention_plan_name }}</span>
                                <span v-else>Not linked to an intervention</span>
                            </div>
                        </v-list-item>
                    </v-list>
                </div>
            </div>
        </fe-dialog>
    </div>
</template>

<script>
    import ErrorState from './ErrorState'
    import Banner from '@/components/common/Banner'
    import {mapState} from 'vuex'

    export default {
        name: 'LinkProgressMonitoring',

        components: {
            ErrorState,
            Banner,
        },

        props: {
            value: {
                type: Boolean, 
                default: false
            },
            formInstance: {
                type: Object,
                default: () => ({}),
                required: true
            },
            fieldRec: {
                type: Object,
                default: () => {}
            },
            linkedPMs: {
                type: Array,
                default: () => [],
            },
        },

        computed: {
            ...mapState('global', ['currentYear']),
            filteredItems() {
                let items = this.existing.length ? this.items.filter(mainItem =>
                    !this.existing.some(filterItem => filterItem?.student_monitor_plan_id === mainItem?.student_monitor_plan_id)
                ) : this.items
                if(this.onlyShowActive) items = items.filter(x => x.school_year_id === this.currentYear.id)
                return items
            },
            showErrorState() {
                return !this.isLoading && !this.filteredItems.length && this.localValue
            }
        },  

        watch: {
            value(v) {
                this.localValue = v
                if (v) {
                    this.getPMs()
                }
            },
            localValue(v) {
                this.$emit('input', v)
            },
            selectedPM(v) {
                if(v) {
                    this.showBanner = false
                }
            },
        },

        data() {
            return {
                onlyShowActive: true,
                localValue: false,
                existing: [],
                items: [],
                forms: [],
                selectedPM: null,
                isLoading: true,
                showBanner: false,
            }
        },

        mounted() {
            if(this.localValue) {
                this.getPMs()
            }
        },

        methods: {
            async getPMs() {
                let p = { student_id: this.formInstance.first_student_id }
                let response = await this.$axios.get(this.$models.getUrl('getLinkedPMs', 'read') + '&' + this.$objectToParams(p))
                this.items = response.data ? response.data.student_monitor_plan : []
                if(!response.data) this.showWarningState = true
                this.existing = this.linkedPMs.length ? this.items.filter(mainItem => 
                    this.linkedPMs.some(filterItem => filterItem.student_monitor_plan_id === mainItem.student_monitor_plan_id)
                ) : []
                this.isLoading = false 
                return this.existing
            },
            getType(name) {
                if(name.length < 30) return name
                return name.slice(0,27) + '...'
            },
            doUnlink(rec) {
                let me = this
                const toRemove = this.linkedPMs.find(x => x.student_monitor_plan_id === rec.student_monitor_plan_id)
                this.$messageBox({
                    title: 'Confirm',
                    persistent: true,
                    maxWidth: 400,
                    message: 'Are you sure you want to unlink this smartFORM?',
                    actions: [{
                        text: 'Cancel'
                    }, {
                        text: 'Unlink',
                        usage: 'danger',
                        onClick: () => {
                            me.$axios.delete('/api/v3/form/form-instance-student-monitor-plan/remove?id=' + toRemove.form_instance_student_monitor_plan_id)
                                .then(r=> {
                                    this.$ecResponse(r)
                                    me.getPMs()
                                    this.$emit('reload')
                                })
                        }
                    }]
                })
            },
            doLink() {
                if(!this.selectedPM) {
                    this.showBanner = true
                } else {
                    const found = this.linkedPMs.find(x => x.form_field_id === this.fieldRec.form_field_id)
                    const formInstanceStudentMonitorPlanId = found?.form_instance_student_monitor_plan_id || null
                    let p = {
                        formInstanceId: this.formInstance.form_instance_id,
                        studentMonitorPlanId: this.selectedPM.student_monitor_plan_id,
                        formFieldId: this.fieldRec.form_field_id,
                        formInstanceStudentMonitorPlanId: formInstanceStudentMonitorPlanId
                    }
                    this.$axios.put('/api/v3/form/form-instance-student-monitor-plan/create', p)
                        .then(response => {
                            this.$ecResponse(response)
                        })
                        .finally(() => {
                            this.localValue = false
                            this.$emit('reload')
                            this.getPMs()
                        })
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .pms-to-link {
        border: 1px solid #E0E1E6;
        border-radius: 8px;
        max-height: 90px;
        display: flex;
        flex-wrap: wrap;
        height: 100%;
        align-items: flex-start;
        padding: 20px;
        font-size: 14px;
        &-subtext {
            color: #495B8F;
            flex-basis: 100%;    
            flex-direction: row;
            display: flex;
        }
        &-radio {
            ::v-deep i {
                font-size: 20px;
            }
            ::v-deep .v-input--selection-controls__input {
                margin-right: 4px;
            }
        }
        &-selected {
            border: 1px solid #006C90;
        }
        &-input {
            ::v-deep .v-input__slot {
                padding: 0 !important;
            }
        }
    }
</style>
